import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Button from "../Components/Button";
import emailjs from "@emailjs/browser";
import ReCAPTCHA from "react-google-recaptcha";
import PageBanner from "../Components/PageBanner";
import {
  addressInfo,
  contactInfo as initialContactInfoData,
  servicesData,
} from "../Constants/Data";

// EmailJS Configuration
const EMAILJS_CONFIG = {
  SERVICE_ID: "service_qcl0p89",
  TEMPLATE_ID: "template_4ul6o07",
  PUBLIC_KEY: "7ilGZ_oY1u8taCJvv",
};

// ReCAPTCHA Site Key
const RECAPTCHA_SITE_KEY = "6LcVdPsqAAAAAFeo84JcbbftJHjL6T0UKf0KCAjQ";

export default function Contact() {
  const [contactInfo, setContactInfoData] = useState([]);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState("");

  useEffect(() => {
    setContactInfoData(initialContactInfoData);
  }, []);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    service: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    setCaptchaError("");
  };

  const validate = (name, value) => {
    let error = "";
    switch (name) {
      case "name":
        error = /^[A-Za-z\s]+$/.test(value) ? "" : "Only alphabets allowed.";
        break;
      case "email":
        error = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)
          ? ""
          : "Invalid email format.";
        break;
      case "phone":
        error = /^\d{10,15}$/.test(value) ? "" : "Invalid phone number.";
        break;
      case "message":
        error = value.trim() ? "" : "Message cannot be empty.";
        break;
      case "service":
        error = value ? "" : "Please select a service.";
        break;
      default:
        break;
    }

    return error;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: validate(name, value) });
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleSubmit = (e) => {
    e.preventDefault();

    let newErrors = {};
    Object.keys(formData).forEach((field) => {
      newErrors[field] = validate(field, formData[field]);
    });

    setErrors(newErrors);

    // Check if reCAPTCHA is completed
    if (!captchaValue) {
      setCaptchaError("Please complete the reCAPTCHA verification!");
      return;
    }

    if (Object.values(newErrors).every((err) => err === "")) {
      setIsSubmitting(true);

      // Get current date and time
      const now = new Date();
      const date = now.toLocaleDateString("en-AU", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      const time = now.toLocaleTimeString("en-AU", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
      const currentYear = now.getFullYear();

      // Prepare template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        date: date,
        time: time,
        current_year: currentYear.toString(),
        selected_options: "General Inquiry / Contact Form",
        "g-recaptcha-response": captchaValue,
      };

      // Send email using EmailJS
      emailjs
        .send(
          EMAILJS_CONFIG.SERVICE_ID,
          EMAILJS_CONFIG.TEMPLATE_ID,
          templateParams,
          EMAILJS_CONFIG.PUBLIC_KEY,
        )
        .then(
          (response) => {
            setSuccessMessage(
              "Message sent successfully! We'll get back to you soon.",
            );
            setFormData({
              name: "",
              email: "",
              phone: "",
              message: "",
              service: "",
            });
            setCaptchaValue(null);
            setCaptchaError("");
            setIsSubmitting(false);

            // Reset reCAPTCHA
            if (window.grecaptcha && window.grecaptcha.reset) {
              window.grecaptcha.reset();
            }
          },
          (error) => {
            // console.error("Email sending failed:", error);
            setSuccessMessage(
              "❌ Failed to send message. Please try again or contact us directly.",
            );
            setIsSubmitting(false);
          },
        )
        .catch((catchError) => {
          // console.error("Unexpected error:", catchError);
          setSuccessMessage("❌ Unexpected error occurred. Please try again.");
          setIsSubmitting(false);
        });
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact | Urban View Real Estate PVT LTD</title>
        <meta
          name="description"
          content="Contact Urban View Real Estate for property inquiries, appraisals, and professional real estate services across Australia."
        />
        <meta
          name="keywords"
          content="Contact Real Estate, Property Inquiry, Real Estate Consultation, Get A Quote, Urban View Contact"
        />
        <meta name="author" content="Urban View Real Estate PVT LTD" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://urbanviewre.com/contact" />

        {/* Open Graph */}
        <meta property="og:title" content="Contact Urban View Real Estate" />
        <meta
          property="og:description"
          content="Get in touch with Urban View Real Estate for professional property services and consultations."
        />
        <meta property="og:site_name" content="Urban View Real Estate" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urbanviewre.com/contact" />
        <meta
          property="og:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact Urban View Real Estate" />
        <meta
          name="twitter:description"
          content="Get in touch with Urban View Real Estate for professional property services and consultations."
        />
        <meta
          name="twitter:image"
          content="https://urbanviewre.com/assets/logo-2-CGgGTWZk.png"
        />
        <meta name="twitter:site" content="@urbanviewre" />
        <meta name="twitter:creator" content="@urbanviewre" />

         {/* Facebook  */}
        <meta
          property="fb:admins"
          content="https://www.facebook.com/profile.php?id=61559035348971"
        />
      </Helmet>

      <PageBanner
        title="Contact Us"
        breadcrumbs={[
          { label: "Home", href: "/", icon: "bi-house-door-fill" },
          { label: "Contact", active: true },
        ]}
      />

      <section className="contact bg-white pt-5">
        <div className="container">
          <div className="row pt-5 g-4 d-flex justify-content-center align-items-stretch">
            {addressInfo.map((item, index) => (
              <div key={index} className="col-12 col-sm-6 col-lg-4">
                <div className="card align-items-center rounded-0 text-center border py-5 px-2 service-card">
                  <h5 className="mb-1 fw-bold brand">{item.title}</h5>

                  {item.details.map((detail, i) => (
                    <p key={i} className="py-1 mb-0 fw-semibold text-muted">
                      <i className="bi bi-geo-alt-fill me-2 text-primary"></i>
                      <a
                        href={detail.href}
                        className="text-muted text-decoration-none"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {detail.label}
                      </a>
                    </p>
                  ))}

                  <h6 className="fw-semibold text-muted">
                    <i className="bi bi-telephone-fill me-2 text-primary"></i>
                    <a
                      href={`tel:${item.number}`}
                      className="text-muted text-decoration-none"
                      target={item.external ? "_blank" : undefined}
                      rel="noopener noreferrer"
                    >
                      {item.number}
                    </a>
                  </h6>
                </div>
              </div>
            ))}
          </div>

          <div
            className="card border-0 rounded-0 shadow p-5 position-relative z-2"
            style={{ bottom: "-150px" }}
          >
            <h3 className="fw-bold brand border-start border-3 border-primary ps-3">
              Contact Us
            </h3>
            <p className="text-muted mt-2">
              Fill out the form below and our team will get back to you shortly.
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <div className="row g-3 mt-3">
                <div className="col-md-6 position-relative pb-3">
                  <input
                    type="text"
                    name="name"
                    className={`form-control border rounded-0 py-3 ${
                      errors.name ? "is-invalid" : ""
                    }`}
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                  <i className="bi bi-person-fill position-absolute end-0 top-50 translate-middle-y pe-4 text-primary"></i>
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>

                <div className="col-md-6 position-relative pb-3">
                  <input
                    type="email"
                    name="email"
                    className={`form-control border rounded-0 py-3 ${
                      errors.email ? "is-invalid" : ""
                    }`}
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                  <i className="bi bi-envelope-fill position-absolute end-0 top-50 translate-middle-y pe-4 text-primary"></i>
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>

                <div className="col-md-6 position-relative py-3">
                  <select
                    name="service"
                    className={`form-select border rounded-0 py-3 ${
                      errors.service ? "is-invalid" : ""
                    }`}
                    value={formData.service}
                    onChange={handleChange}
                    required
                  >
                    <option value="" disabled>
                      Select Service Type
                    </option>
                    {servicesData.map((service) => (
                      <option key={service.id} value={service.title}>
                        {service.title}
                      </option>
                    ))}
                  </select>
                  <i className="bi bi-caret-down-fill position-absolute end-0 top-50 translate-middle-y pe-4 text-primary"></i>
                  {errors.service && (
                    <div className="invalid-feedback">{errors.service}</div>
                  )}
                </div>

                <div className="col-md-6 position-relative py-3">
                  <input
                    type="tel"
                    name="phone"
                    className={`form-control border rounded-0 py-3 ${
                      errors.phone ? "is-invalid" : ""
                    }`}
                    placeholder="Enter phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                  <i className="bi bi-telephone-fill position-absolute end-0 top-50 translate-middle-y pe-4 text-primary"></i>
                  {errors.phone && (
                    <div className="invalid-feedback">{errors.phone}</div>
                  )}
                </div>

                <div className="col-12 position-relative pb-3">
                  <textarea
                    name="message"
                    className={`form-control border rounded-0 py-3 ${
                      errors.message ? "is-invalid" : ""
                    }`}
                    rows="5"
                    placeholder="Enter your message..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                  ></textarea>
                  <i className="bi bi-pencil-fill position-absolute end-0 top-0 mt-3 pe-4 text-primary"></i>
                  {errors.message && (
                    <div className="invalid-feedback">{errors.message}</div>
                  )}
                </div>

                {/* reCAPTCHA */}
                <div className="col-12 pb-3">
                  <ReCAPTCHA
                    sitekey={RECAPTCHA_SITE_KEY}
                    onChange={handleCaptchaChange}
                  />
                  {captchaError && (
                    <p className="text-danger mt-2">{captchaError}</p>
                  )}
                </div>

                <div className="col-12">
                  <Button
                    text={isSubmitting ? "Sending..." : "SEND MESSAGE"}
                    className="text-muted"
                    type="submit"
                    disabled={isSubmitting}
                  />

                  {successMessage && (
                    <div
                      className={`alert ${successMessage.includes("") ? "alert-success" : "alert-danger"} mt-3`}
                      role="alert"
                    >
                      {successMessage}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>

        <div className="ratio ratio-16x9 mt-5" style={{ height: "500px" }}>
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3317.2843216434435!2d150.697758!3d-33.753318!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b1285f4ae6ba947%3A0x8b9684673a5b996!2sSuite%205%2F429%20High%20St%2C%20Penrith%20NSW%202750!5e0!3m2!1sen!2sau!4v1738924590197!5m2!1sen!2sau"
            width="100%"
            style={{ border: "0" }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Urban View Real Estate Location"
          ></iframe>
        </div>
      </section>
    </>
  );
}
